import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { cn } from '../../utils/cn';

export interface AutoExpandingTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  minRows?: number;
  maxRows?: number;
  maxHeight?: number; // in pixels
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const AutoExpandingTextarea = forwardRef<HTMLTextAreaElement, AutoExpandingTextareaProps>(
  ({ 
    value, 
    onChange, 
    minRows = 2, 
    maxRows = 8, 
    maxHeight,
    className = '', 
    placeholder,
    disabled,
    ...props 
  }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const hiddenTextareaRef = useRef<HTMLTextAreaElement>(null);

    // Expose the textarea ref to parent components
    useImperativeHandle(ref, () => textareaRef.current!, []);

    const adjustHeight = () => {
      const textarea = textareaRef.current;
      const hiddenTextarea = hiddenTextareaRef.current;
      
      if (!textarea || !hiddenTextarea) return;

      // Reset height to auto to get the scrollHeight
      textarea.style.height = 'auto';
      hiddenTextarea.style.height = 'auto';

      // Copy content to hidden textarea to measure
      hiddenTextarea.value = value;
      
      // Calculate the line height
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computedStyle.lineHeight, 10) || 20;
      const paddingTop = parseInt(computedStyle.paddingTop, 10) || 0;
      const paddingBottom = parseInt(computedStyle.paddingBottom, 10) || 0;
      const borderTop = parseInt(computedStyle.borderTopWidth, 10) || 0;
      const borderBottom = parseInt(computedStyle.borderBottomWidth, 10) || 0;

      // Calculate min and max heights
      const minHeight = (lineHeight * minRows) + paddingTop + paddingBottom + borderTop + borderBottom;
      const maxHeightCalc = maxHeight || ((lineHeight * maxRows) + paddingTop + paddingBottom + borderTop + borderBottom);

      // Get the scroll height (content height)
      const scrollHeight = hiddenTextarea.scrollHeight;
      
      // Set the height based on content, constrained by min/max
      let newHeight = Math.max(minHeight, scrollHeight);
      newHeight = Math.min(newHeight, maxHeightCalc);

      textarea.style.height = `${newHeight}px`;
    };

    // Adjust height when value changes
    useEffect(() => {
      adjustHeight();
    }, [value, minRows, maxRows, maxHeight]);

    // Adjust height on window resize
    useEffect(() => {
      const handleResize = () => adjustHeight();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e);
      // Adjust height after state update
      setTimeout(adjustHeight, 0);
    };

    const baseClasses = cn(
      // Base styles
      "w-full resize-none transition-all duration-200 ease-in-out",
      "border border-gray-300 dark:border-gray-600 rounded-lg",
      "bg-white dark:bg-gray-800",
      "text-gray-900 dark:text-gray-100",
      "placeholder-gray-500 dark:placeholder-gray-400",
      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
      "px-3 py-2",
      
      // Disabled styles
      disabled && "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-900",
      
      // Custom classes
      className
    );

    return (
      <div className="relative">
        {/* Main textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className={baseClasses}
          style={{
            overflow: 'hidden',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap'
          }}
          {...props}
        />
        
        {/* Hidden textarea for measuring content height */}
        <textarea
          ref={hiddenTextareaRef}
          className={cn(baseClasses, "absolute top-0 left-0 opacity-0 pointer-events-none")}
          style={{
            height: 'auto',
            overflow: 'hidden',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            zIndex: -1
          }}
          tabIndex={-1}
          readOnly
        />
      </div>
    );
  }
);

AutoExpandingTextarea.displayName = 'AutoExpandingTextarea'; 